import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, LifeBuoy, Plus, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { canCreateTickets, canSeeTickets, ticketErrorMessage } from "../../lib/tickets";
import { DelayCell, PriorityBadge, TicketStatusBadge } from "./TicketBits";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Skeleton } from "../ui/skeleton";

const SHOWN = 5;

// The « Support » tab of a pharmacy (fiche and customer account, D-72): its open tickets the READER may see — the tickets list's own
// scope —, the way to all of them, and « Nouveau ticket » prefilled. Renders nothing for someone who cannot see tickets.
// With `linkedTo` ({mission_id} or {assist_id}, 08-3) it lists instead the tickets — open or not — that link that object; `extra(reload)`
// renders an action beside « Créer un ticket » that can ask the list to reload.
export default function PharmacyTickets({ prospectId, pharmacyName = null, compact = false, linkedTo = null, newTicketLink = null, extra = null }) {
  const { user } = useAuth();
  const [state, setState] = useState({ phase: "loading", items: [], total: 0, error: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const visible = canSeeTickets(user);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, phase: "loading" }));
    try {
      const params = linkedTo ? { ...linkedTo, limit: SHOWN } : { prospect_id: prospectId, state: "open", limit: SHOWN };
      const { data } = await api.get("/tickets", { params });
      setState({ phase: "ready", items: data.items, total: data.total, error: "" });
    } catch (e) {
      setState({ phase: "error", items: [], total: 0, error: ticketErrorMessage(e, "Impossible de charger les tickets.") });
    }
  }, [prospectId, linkedTo?.mission_id, linkedTo?.assist_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (visible) load(); }, [visible, load, reloadKey]);
  if (!visible) return null;

  return (
    <Card className="p-5 space-y-3" data-testid="pharmacy-tickets">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <LifeBuoy size={15} className="text-slate-400" />{linkedTo ? "Tickets support liés" : "Support"}
        </h2>
        <div className="flex flex-wrap justify-end gap-2">
        {extra && extra(() => setReloadKey((k) => k + 1))}
        {canCreateTickets(user) && (!linkedTo || newTicketLink) && (
          <Button variant="outline" size="sm" asChild data-testid="pharmacy-tickets-new">
            <Link to={newTicketLink || `/support/tickets/nouveau?prospect=${prospectId}`}><Plus size={14} className="mr-1" />{linkedTo ? "Créer un ticket" : "Nouveau ticket"}</Link>
          </Button>
        )}
        </div>
      </div>
      {state.phase === "loading" && <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>}
      {state.phase === "error" && (
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-500" />{state.error}
          <Button variant="ghost" size="sm" onClick={load}><RefreshCw size={13} className="mr-1" />Réessayer</Button>
        </div>
      )}
      {state.phase === "ready" && state.items.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="pharmacy-tickets-empty">
          {linkedTo ? "Aucun ticket que vous pouvez voir n'est relié ici." : "Aucun ticket ouvert que vous pouvez voir pour cette pharmacie."}
        </p>
      )}
      {state.phase === "ready" && state.items.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {state.items.map((t) => (
            <li key={t.id}>
              <Link to={`/support/tickets/${t.id}`} className="flex items-start gap-3 py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`pharmacy-ticket-${t.number}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="text-sm font-medium text-slate-900 break-words">{t.title}</div>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                    <span className="tabular-nums">{t.number}</span>
                    <PriorityBadge priority={t.priority} adjusted={t.priority_adjusted} />
                    {(!compact || linkedTo) && <TicketStatusBadge status={t.status} />}
                    <DelayCell ticket={t} />
                  </div>
                </div>
                <ChevronRight size={15} className="text-slate-300 shrink-0 mt-1" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {/* Without any open ticket the useful list is the history: the link says so and opens it (the name rides along so the
          list can title itself even when it is empty). */}
      {!linkedTo && state.phase === "ready" && (
        <Link to={`/support/tickets?prospect=${prospectId}&state=${state.total ? "open" : "done"}`} state={{ pharmacyName }}
          className="inline-block text-sm text-jamora hover:underline" data-testid="pharmacy-tickets-all">
          {state.total > SHOWN ? `Voir les ${state.total} tickets ouverts` : state.total ? "Voir les tickets de la pharmacie" : "Voir les tickets résolus et clos"}
        </Link>
      )}
    </Card>
  );
}
